import {useCallback, useEffect, useState} from 'react';

import type {RunSelectorRunsPage} from '../../../state/runSelectorRuns/actions';
import {encodeCursor} from '../../PaginatedList/utils';

/**
 * How a query pages, from the `run_selector_offset_pagination_user` gate: `offset` fetches only the page shown, `growingLimit` fetches every run from the first page through the page shown.
 */
export type RunsPagingMode = 'growingLimit' | 'offset';

export type RunsPaging = {
  after?: string;
  limit: number;
  onPageChange(page: number): void;
};

const useGrowingLimitPaging = (pageSize: number): RunsPaging => {
  // Paging works by setting the `limit` query variable, which determines
  // how many runs we get back. When the user clicks "next page" or selects
  // a larger page size, all we're really doing is setting a higher `limit`.
  const [limit, setLimit] = useState(pageSize);

  // To avoid triggering unnecessary requests when selecting a smaller page size
  // or visiting a page that only has runs we've already seen, we should only update
  // `limit` if the new value would be larger (and therefore include new results).
  const setLimitMax = (potentialLimit: number) => {
    setLimit(curLimit => Math.max(curLimit, potentialLimit));
  };

  // This is called when user pages forward
  const loadMoreRuns = useCallback(
    (page: number) => setLimitMax(page * pageSize),
    [pageSize]
  );

  // Make sure `limit` is up to date if page size changes
  useEffect(() => {
    setLimitMax(pageSize);
  }, [pageSize]);

  return {limit, onPageChange: loadMoreRuns};
};

const useOffsetPaging = (pageSize: number, page?: number): RunsPaging => {
  const [reportedPage, setReportedPage] = useState(1);

  const after = getRunsPageCursor(page ?? reportedPage, pageSize);
  return {after, limit: pageSize, onPageChange: setReportedPage};
};

/**
 * Main entry point for useRunsPaging, switching based off of paging mode.
 * Returns paging params for the query based off of paging mode.
 *
 * - `offset`: `limit` is the page size and `after` is the offset of the page shown. The top-level table reports its page through onPageChange and resets to page 1 on a page size change, so the page here follows. An expanded group tracks its own page, so it passes `page` and onPageChange goes unused.
 * - `growingLimit`: no `after`, and onPageChange raises `limit` to cover every run through that page, so paging back or to a smaller page size reuses runs already loaded
 * - both modes always run, so the gate resolving after mount never changes the hook order. Page 1 sends the same `after` and `limit` in both, so a late gate doesn't refetch it.
 */
export const useRunsPaging = (
  pageSize: number,
  mode: RunsPagingMode,
  page?: number
): RunsPaging => {
  const growingLimitPaging = useGrowingLimitPaging(pageSize);
  const offsetPaging = useOffsetPaging(pageSize, page);

  return mode === 'offset' ? offsetPaging : growingLimitPaging;
};

/**
 * getRunsPageCursor returns the `after` cursor for a page of runs, or undefined for page 1.
 *
 * - project.runs decodes `after` as an offset, not a row: `arrayconnection:<n>` starts the page at run n + 1 (offsetFromAfter in services/gorilla/api/resolver/query.go)
 * - so any page's cursor can be computed directly, without the previous page's endCursor
 */
export function getRunsPageCursor(
  page: number,
  pageSize: number
): string | undefined {
  if (page <= 1) {
    return undefined;
  }

  const lastOffsetBeforePage = (page - 1) * pageSize - 1;
  return encodeCursor(`arrayconnection:${lastOffsetBeforePage}`);
}

function isSamePage(
  left: RunSelectorRunsPage,
  right: RunSelectorRunsPage
): boolean {
  // an absent cursor and a null one both mean page 1
  return (
    (left.after ?? null) === (right.after ?? null) && left.limit === right.limit
  );
}

type IsOtherPageLoadedParams = {
  hasRequestFailed: boolean;
  isRootQuery: boolean;
  loadedPage?: RunSelectorRunsPage;
  requestedPage?: RunSelectorRunsPage;
};
/**
 * isOtherPageLoaded reports whether redux holds a different page than the one asked for
 * - the top-level table hides them as soon as the page changes
 * - an expanded group keeps them until the request for its next page fails, so the rows below it don't jump on every click. Apollo stops loading a render before the response reaches redux, so loading alone can't tell a failure from a page about to land.
 */
export function isOtherPageLoaded({
  hasRequestFailed,
  isRootQuery,
  loadedPage,
  requestedPage,
}: IsOtherPageLoadedParams): boolean {
  if (loadedPage == null || requestedPage == null) {
    return false;
  }

  if (isSamePage(loadedPage, requestedPage)) {
    return false;
  }

  return isRootQuery || hasRequestFailed;
}

type GetPageRowsParams<Row> = {
  currentPage: number;
  pageSize: number;
  pagingMode: RunsPagingMode;
  rows: Row[];
};

/**
 * getPageRows returns the rows to show for the current page.
 *
 * - `offset`: the runs query already returned only this page, so the rows are shown as they are
 * - `growingLimit`: the rows hold every run from the first page through this one, so the page is sliced out of them
 */
export function getPageRows<Row>({
  currentPage,
  pageSize,
  pagingMode,
  rows,
}: GetPageRowsParams<Row>): Row[] {
  if (pagingMode === 'offset') {
    return rows;
  }

  const pageStart = (currentPage - 1) * pageSize;
  return rows.slice(pageStart, pageStart + pageSize);
}

type IsPastLastPageParams = {
  currentPage: number;
  pageSize: number;
  pagingMode: RunsPagingMode;
  totalRowCount: number;
};
/**
 * isPastLastPage reports whether the page shown starts after the last row, as when every run on the last page is deleted between polls.
 *
 * - `offset` only: `growingLimit` keeps master's behavior until the gate is removed
 * - a total of 0 means nothing matches, so there is no last page to step back to
 */
export function isPastLastPage({
  currentPage,
  pageSize,
  pagingMode,
  totalRowCount,
}: IsPastLastPageParams): boolean {
  if (pagingMode !== 'offset' || totalRowCount <= 0) {
    return false;
  }

  const lastPage = Math.ceil(totalRowCount / pageSize);
  return currentPage > lastPage;
}

export type GroupChildrenQueryPage = {
  page?: number;
  pageSize: number;
};

type GetGroupChildrenQueryPageParams = {
  activePage: number;
  pageSize: number;
  pagingMode: RunsPagingMode;
};

/**
 * getGroupChildrenQueryPage returns the page and page size an expanded group's children query asks for.
 *
 * - `offset`: one page of children, at the group's active page
 * - `growingLimit`: every child through two pages past the active page, so the next click is already loaded
 */
export function getGroupChildrenQueryPage({
  activePage,
  pageSize,
  pagingMode,
}: GetGroupChildrenQueryPageParams): GroupChildrenQueryPage {
  if (pagingMode === 'offset') {
    return {page: activePage, pageSize};
  }

  return {pageSize: pageSize * (activePage + 2)};
}
